import React, { useMemo, useRef } from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import GridTouch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { DashboardProductionDetailDto } from '../../Dto_Home';

const PRODUCTION_COLUMN_STATE_KEY = 'home-c-production-detail-column-state';
const ignoreDashboardGridState = () => undefined;

interface Props {
  productionDetailData: DashboardProductionDetailDto[];
}

const productionDetailColumns: ColDef[] = [
  ...getDefaultRowIndexColumnConfig((params) => {
    if (params.node.rowPinned === 'bottom') return '';
    return params.node.rowIndex + 1;
  }),
  {
    field: 'groupKey',
    headerName: '생산일자',
    width: 150,
    valueFormatter: (params) => params.value || '-',
    cellStyle: (params) =>
      params.node.rowPinned === 'bottom'
        ? {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            fontWeight: 600,
          }
        : undefined,
  },
  { field: 'itemNm', headerName: '품목명', width: 120 },
  { field: 'itemSpec', headerName: '규격', width: 120 },
  { field: 'modelNo', headerName: '모델번호', width: 120 },
  {
    field: 'productQty',
    headerName: '생산량',
    width: 100,
    valueFormatter: (params) =>
      typeof params.value === 'number' ? params.value.toLocaleString() : (params.value || ''),
    cellStyle: { textAlign: 'right' },
  },
  { field: 'badQty', headerName: '불량', width: 100, cellStyle: { color: 'red', textAlign: 'right' } },
  {
    field: 'acceptQty',
    headerName: '입고량',
    width: 100,
    valueFormatter: (params) =>
      typeof params.value === 'number' ? params.value.toLocaleString() : (params.value || ''),
    cellStyle: { textAlign: 'right' },
  },
];

const defaultColDef: ColDef = { resizable: true, sortable: true };

const ProductionDetailSection_C: React.FC<Props> = ({ productionDetailData }) => {
  const productionGridRef = useRef<AgGridReact<DashboardProductionDetailDto> | null>(null);
  const productionPinnedBottomRow = useMemo(() => [{
    rowIndex: '',
    groupKey: `${productionDetailData.length.toLocaleString()}건`,
    productQty: productionDetailData.reduce(
      (sum, item) => sum + (Number(item.productQty) || 0), 0
    ),
  }], [productionDetailData]);

  return (
    <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col flex-1 min-h-0">
      <h2 className="text-lg font-semibold mb-2">상세 생산실적</h2>
      <div className="ag-theme-alpine flex-1 min-h-0">
        <GridContextWrapper<DashboardProductionDetailDto>
          gridRef={productionGridRef}
          paddedGridData={productionDetailData}
          baseColumnDefs={productionDetailColumns}
          setColumnState={ignoreDashboardGridState}
          localStorageKey={PRODUCTION_COLUMN_STATE_KEY}
          enableCellCalculation
          enableDataHistory={false}
        >
          <GridTouch<DashboardProductionDetailDto>
            ref={productionGridRef}
            columnDefs={productionDetailColumns}
            rowData={productionDetailData}
            pinnedBottomRowData={productionPinnedBottomRow}
            defaultColDef={defaultColDef}
            setHoveredRowIndex={ignoreDashboardGridState}
            columnStateKey={PRODUCTION_COLUMN_STATE_KEY}
            setColumnState={ignoreDashboardGridState}
          />
        </GridContextWrapper>
      </div>
    </div>
  );
};

export default ProductionDetailSection_C;
